/**
 * AccountManagement.tsx - 账户管理页面
 * 管理Email/Twitter/Telegram发送账户，包括创建、编辑、测试连接、重置配额等功能
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Progress,
  Tooltip,
  message,
  Popconfirm,
  Descriptions,
  Drawer,
  Alert,
  Statistic,
  Row,
  Col,
  Badge,
  Typography,
  Tabs,
  Input,
  Form,
  Checkbox,
} from 'antd';
import { useAuthStore } from '../store';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  MailOutlined,
  TwitterOutlined,
  SendOutlined,
  ApiOutlined,
  SafetyOutlined,
  ThunderboltOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  SyncOutlined,
  QrcodeOutlined,
  KeyOutlined,
  QrcodeOutlined,
} from '@ant-design/icons';
import { useAccounts } from '../hooks';
import { accountApi } from '../api/client';
import type { AccountState } from '../types';
import dayjs from 'dayjs';
import TelegramQRAuth from '../components/TelegramQRAuth';
import {
  EmailAccountForm,
  TwitterAccountForm,
  TelegramAccountForm,
  AccountHealthBadge,
} from '../components/AccountConfigForm';

const { Text } = Typography;

// ==================== BD 简化视图组件 ====================

const BDAccountSummary: React.FC = () => {
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [qrAuthVisible, setQrAuthVisible] = useState(false);
  const [qrAuthApiId, setQrAuthApiId] = useState('');
  const [qrAuthApiHash, setQrAuthApiHash] = useState('');
  const [qrAuthSessionName, setQrAuthSessionName] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const data = await accountApi.getSummary();
      setSummary(data);
      // 检查是否已设置邮箱密码
      try {
        const { userConfigApi } = await import('../api/client');
        const config = await userConfigApi.get();
        setPasswordSaved(!!config?.email_app_password);
      } catch { /* ignore */ }
    } catch { /* ignore */ }
    setLoading(false);
  };

  const handleSaveEmailPassword = async () => {
    if (!emailPassword.trim()) {
      message.warning('请输入应用专属密码');
      return;
    }
    setSavingPassword(true);
    try {
      const { userConfigApi } = await import('../api/client');
      await userConfigApi.update({ email_app_password: emailPassword.trim() });
      message.success('邮箱应用专属密码已保存');
      setPasswordSaved(true);
      setEmailPassword('');
      loadSummary();
    } catch (error: any) {
      message.error(`保存失败: ${error.message || '未知错误'}`);
    }
    setSavingPassword(false);
  };

  React.useEffect(() => { loadSummary(); }, []);

  const handleTelegramAuth = async () => {
    try {
      const defaults = await accountApi.getSystemDefaults();
      // 系统预设的 API 凭证，BD 不需要手动输入
      setQrAuthApiId(defaults.telegram?.api_id || '');
      setQrAuthApiHash(defaults.telegram?.api_hash || '');
      setQrAuthSessionName(defaults.telegram?.account_name || 'bd_telegram');
      setQrAuthVisible(true);
    } catch {
      message.error('无法获取系统配置');
    }
  };

  const healthTag = (health: string) => {
    const map: Record<string, { color: string; text: string }> = {
      healthy: { color: 'success', text: '正常' },
      active: { color: 'success', text: '正常' },
      warning: { color: 'warning', text: '警告' },
      rate_limited: { color: 'warning', text: '限流中' },
      restricted: { color: 'warning', text: '受限' },
      suspended: { color: 'error', text: '已暂停' },
      not_configured: { color: 'default', text: '未配置' },
    };
    const cfg = map[health] || { color: 'default', text: health };
    return <Tag color={cfg.color}>{cfg.text}</Tag>;
  };

  if (loading) return <Card loading />;
  if (!summary) return <Card><Alert type="error" message="无法加载账户信息" /></Card>;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Typography.Title level={4}>账号管理</Typography.Title>
        <Text type="secondary">系统凭证由管理员统一配置，您只需关注账户状态。</Text>
      </div>

      <Row gutter={[16, 16]}>
        {/* 邮件 */}
        <Col xs={24} md={8}>
          <Card title={<><MailOutlined style={{ color: '#1890ff', marginRight: 8 }} />邮件</>}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="发件邮箱">{summary.email?.account_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="状态">{healthTag(summary.email?.health)}</Descriptions.Item>
              <Descriptions.Item label="今日用量">
                <Progress
                  percent={Math.round((summary.email?.daily_usage / summary.email?.daily_limit) * 100)}
                  size="small"
                  format={() => `${summary.email?.daily_usage}/${summary.email?.daily_limit}`}
                />
              </Descriptions.Item>
              <Descriptions.Item label="累计发送">{summary.email?.total_sent || 0}</Descriptions.Item>
            </Descriptions>
            {!passwordSaved ? (
              <div style={{ marginTop: 12 }}>
                <Alert
                  type="warning"
                  message="请设置邮箱应用专属密码"
                  description={
                    <span>
                      登录 Google Account → Security → 2-Step Verification → App Passwords → 生成 16 位密码
                    </span>
                  }
                  showIcon
                  style={{ marginBottom: 8 }}
                />
                <Space.Compact style={{ width: '100%' }}>
                  <Input.Password
                    placeholder="粘贴 16 位应用专属密码"
                    value={emailPassword}
                    onChange={e => setEmailPassword(e.target.value)}
                  />
                  <Button type="primary" loading={savingPassword} onClick={handleSaveEmailPassword}>
                    保存
                  </Button>
                </Space.Compact>
              </div>
            ) : (
              <Tag color="success" style={{ marginTop: 8 }}>应用专属密码已设置</Tag>
            )}
          </Card>
        </Col>

        {/* Twitter */}
        <Col xs={24} md={8}>
          <Card title={<><TwitterOutlined style={{ color: '#1DA1F2', marginRight: 8 }} />Twitter</>}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="账户">{summary.twitter?.account_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="状态">{healthTag(summary.twitter?.health)}</Descriptions.Item>
              <Descriptions.Item label="今日用量">
                <Progress
                  percent={Math.round((summary.twitter?.daily_usage / summary.twitter?.daily_limit) * 100)}
                  size="small"
                  format={() => `${summary.twitter?.daily_usage}/${summary.twitter?.daily_limit}`}
                />
              </Descriptions.Item>
              <Descriptions.Item label="累计发送">{summary.twitter?.total_sent || 0}</Descriptions.Item>
            </Descriptions>
            {!summary.system_configured?.twitter && (
              <Alert type="warning" message="Twitter API 未配置，请联系管理员" showIcon style={{ marginTop: 8 }} />
            )}
          </Card>
        </Col>

        {/* Telegram */}
        <Col xs={24} md={8}>
          <Card title={<><SendOutlined style={{ color: '#9254de', marginRight: 8 }} />Telegram</>}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="账户">{summary.telegram?.account_name || '未授权'}</Descriptions.Item>
              <Descriptions.Item label="状态">{healthTag(summary.telegram?.health)}</Descriptions.Item>
              <Descriptions.Item label="今日用量">
                <Progress
                  percent={Math.round((summary.telegram?.daily_usage / summary.telegram?.daily_limit) * 100)}
                  size="small"
                  format={() => `${summary.telegram?.daily_usage}/${summary.telegram?.daily_limit}`}
                />
              </Descriptions.Item>
              <Descriptions.Item label="累计发送">{summary.telegram?.total_sent || 0}</Descriptions.Item>
            </Descriptions>
            {summary.telegram?.health === 'not_configured' && (
              <Button
                type="primary"
                icon={<QrcodeOutlined />}
                onClick={handleTelegramAuth}
                style={{ marginTop: 12 }}
                block
              >
                扫码授权 Telegram
              </Button>
            )}
          </Card>
        </Col>
      </Row>

      <Card size="small" style={{ marginTop: 16, background: '#fafafa' }}>
        <Text type="secondary">
          系统限额：邮件 {summary.email?.daily_limit}/天 | Twitter {summary.twitter?.daily_limit}/天 | Telegram {summary.telegram?.daily_limit}/天
          &nbsp;·&nbsp;限额由系统管理员统一配置
        </Text>
      </Card>

      {/* Telegram QR Auth — 组件自带 Modal，不需要外层包裹 */}
      <TelegramQRAuth
        visible={qrAuthVisible}
        onClose={() => setQrAuthVisible(false)}
        apiId={qrAuthApiId}
        apiHash={qrAuthApiHash}
        sessionName={qrAuthSessionName}
        onSuccess={() => {
          setQrAuthVisible(false);
          message.success('Telegram 授权成功');
          loadSummary();
        }}
      />
    </div>
  );
};

// ==================== 主组件（Admin 完整视图 / BD 简化视图） ====================

// 渠道配置
const channelConfig: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  email: { icon: <MailOutlined />, color: 'blue', label: 'Email' },
  twitter: { icon: <TwitterOutlined />, color: 'cyan', label: 'Twitter' },
  telegram: { icon: <SendOutlined />, color: 'purple', label: 'Telegram' },
};

// 状态配置
const statusConfig: Record<string, { color: string; text: string }> = {
  active: { color: 'success', text: '正常' },
  warning: { color: 'warning', text: '警告' },
  rate_limited: { color: 'warning', text: '限流中' },
  suspended: { color: 'error', text: '已暂停' },
  error: { color: 'error', text: '错误' },
};

const AccountManagement: React.FC = () => {
  // BD 角色显示简化视图，Admin 显示完整管理视图
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  if (!isAdmin) {
    return <BDAccountSummary />;
  }

  // ==================== Admin 完整视图以下 ====================
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<AccountState | null>(null);
  const [testingAccount, setTestingAccount] = useState<string | null>(null);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});

  // Telegram QR授权状态
  const [qrAuthVisible, setQrAuthVisible] = useState(false);
  const [qrAuthApiId, setQrAuthApiId] = useState('');
  const [qrAuthApiHash, setQrAuthApiHash] = useState('');
  const [qrAuthSessionName, setQrAuthSessionName] = useState('');

  // API凭据输入弹窗状态
  const [credentialsModalVisible, setCredentialsModalVisible] = useState(false);
  const [credentialsAccountId, setCredentialsAccountId] = useState('');
  const [credentialsAccountName, setCredentialsAccountName] = useState('');
  const [inputApiId, setInputApiId] = useState('');
  const [inputApiHash, setInputApiHash] = useState('');
  const [saveCredentialsChecked, setSaveCredentialsChecked] = useState(true);

  // 数据查询
  const { data: accountsData, isLoading, refetch } = useAccounts();

  // API returns array directly, not wrapped in {items: [...]}
  // Also map backend field names to frontend expected names
  const healthToStatus: Record<string, string> = {
    healthy: 'active',
    warming: 'active',
    degraded: 'warning',
    restricted: 'rate_limited',
    suspended: 'suspended',
  };

  const accounts = (accountsData || []).map((acc: any) => ({
    ...acc,
    // Map backend field names to frontend expected names
    name: acc.account_name || acc.name,
    channel: acc.account_type || acc.channel,
    status: healthToStatus[acc.health] || acc.status || 'active',
    is_active: acc.health !== 'suspended' && acc.health !== 'restricted',
    daily_sent: acc.daily_usage_count ?? acc.daily_sent ?? 0,
    daily_limit: acc.daily_limit ?? 50, // Default limit if not specified
    total_sent: acc.total_usage_count ?? acc.total_sent ?? 0,
    last_used_at: acc.last_usage_at || acc.last_used_at,
    credentials: acc.credentials || acc.config || {},
    has_credentials: acc.has_credentials ?? false,
    error_message: acc.error_message || null,
  }));

  // 统计数据
  const stats = {
    total: accounts.length,
    active: accounts.filter(a => a.status === 'active').length,
    rateLimited: accounts.filter(a => a.status === 'rate_limited').length,
    error: accounts.filter(a => a.status === 'error' || a.status === 'suspended').length,
  };

  // 测试账户连接
  const handleTestConnection = async (accountId: string) => {
    setTestingAccount(accountId);
    try {
      const result = await accountApi.testConnection(accountId);
      if (result.success) {
        message.success('连接测试成功');
      } else {
        message.error(`连接测试失败: ${result.error}`);
      }
    } catch (error: any) {
      message.error(`测试失败: ${error.message}`);
    } finally {
      setTestingAccount(null);
    }
  };

  // 重置每日配额
  const handleResetDailyUsage = async (accountId: string) => {
    try {
      await accountApi.resetDailyUsage(accountId);
      message.success('每日配额已重置');
      refetch();
    } catch (error: any) {
      message.error(`重置失败: ${error.message}`);
    }
  };

  // 解除限制
  const handleUnrestrict = async (accountId: string) => {
    try {
      await accountApi.unrestrict(accountId);
      message.success('限制已解除');
      refetch();
    } catch (error: any) {
      message.error(`解除限制失败: ${error.message}`);
    }
  };

  // 删除账户
  const handleDelete = async (accountId: string) => {
    try {
      await accountApi.delete(accountId);
      message.success('账户已删除');
      refetch();
    } catch (error: any) {
      message.error(`删除失败: ${error.message}`);
    }
  };

  // 打开编辑弹窗
  const openEditModal = (account: AccountState) => {
    setSelectedAccount(account);
    setEditModalVisible(true);
  };

  // 打开详情抽屉
  const openDetailDrawer = (account: AccountState) => {
    setSelectedAccount(account);
    setDetailDrawerVisible(true);
  };

  // 切换密钥显示
  const toggleSecretVisibility = (key: string) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // 处理QR授权按钮点击
  const handleQRAuthClick = async (account: AccountState) => {
    const accAny = account as any;

    // 如果 DB 中已保存凭据，直接加载后开始 QR 授权（无需用户重新输入）
    if (accAny.has_credentials) {
      try {
        const creds = await accountApi.getCredentials(account.id);
        startTelegramQRAuth(creds.api_id, creds.api_hash, account.name);
        return;
      } catch {
        // 加载失败时回退到手动输入
      }
    }

    // 无已保存凭据，或加载失败：显示输入弹窗，预填充 config 中的 api_id/hash（若有）
    const apiId = accAny.credentials?.api_id || '';
    const apiHash = accAny.credentials?.api_hash || '';
    setCredentialsAccountId(account.id);
    setCredentialsAccountName(account.name);
    setInputApiId(String(apiId));
    setInputApiHash(String(apiHash));
    setSaveCredentialsChecked(true);
    setCredentialsModalVisible(true);
  };

  // 开始Telegram QR授权
  const startTelegramQRAuth = (apiId: string, apiHash: string, sessionName: string) => {
    setQrAuthApiId(apiId);
    setQrAuthApiHash(apiHash);
    setQrAuthSessionName(sessionName);
    setQrAuthVisible(true);
  };

  // 确认输入的API凭据
  const handleCredentialsConfirm = async () => {
    if (!inputApiId || !inputApiHash) {
      message.error('请输入完整的API凭据');
      return;
    }
    // 可选：保存凭据到 DB
    if (saveCredentialsChecked && credentialsAccountId) {
      try {
        await accountApi.saveCredentials(credentialsAccountId, inputApiId, inputApiHash);
        refetch();
      } catch (err: any) {
        message.warning(`凭据保存失败，但将继续授权: ${err.message}`);
      }
    }
    setCredentialsModalVisible(false);
    startTelegramQRAuth(inputApiId, inputApiHash, credentialsAccountName);
  };

  // Telegram QR授权成功
  const handleQRAuthSuccess = (user: any) => {
    message.success(`Telegram账户 ${user.username || user.first_name} 授权成功`);
    refetch();
  };

  // 表格列定义
  const columns = [
    {
      title: '账户名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: AccountState) => {
        const accAny = record as any;
        return (
          <Space>
            <span className={`channel-icon channel-${record.channel}`}>
              {channelConfig[record.channel]?.icon}
            </span>
            <a onClick={() => openDetailDrawer(record)}>{text}</a>
            {record.channel === 'telegram' && accAny.has_credentials && (
              <Tooltip title="已保存API凭据，扫码授权时自动填充">
                <Tag icon={<KeyOutlined />} color="green" style={{ fontSize: 11 }}>
                  已保存凭据
                </Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: '渠道',
      dataIndex: 'channel',
      key: 'channel',
      width: 100,
      render: (channel: string) => (
        <Tag color={channelConfig[channel]?.color}>
          {channelConfig[channel]?.label}
        </Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: string, record: AccountState) => {
        // 使用AccountHealthBadge组件
        const healthMap: Record<string, string> = {
          active: 'healthy',
          warning: 'warning',
          rate_limited: 'restricted',
          suspended: 'suspended',
          error: 'restricted',
        };
        return (
          <Space size={4}>
            <AccountHealthBadge health={healthMap[status] || 'healthy'} />
            {!record.is_active && <Tag color="default">已禁用</Tag>}
          </Space>
        );
      },
    },
    {
      title: '今日使用',
      key: 'usage',
      width: 200,
      render: (_: any, record: AccountState) => {
        const percent = Math.round((record.daily_sent / record.daily_limit) * 100);
        const status = percent >= 90 ? 'exception' : percent >= 70 ? 'active' : 'success';
        return (
          <Tooltip title={`${record.daily_sent} / ${record.daily_limit}`}>
            <Progress
              percent={percent}
              size="small"
              status={status}
              format={() => `${record.daily_sent}/${record.daily_limit}`}
            />
          </Tooltip>
        );
      },
    },
    {
      title: '总发送量',
      dataIndex: 'total_sent',
      key: 'total_sent',
      width: 100,
      render: (val: number) => val?.toLocaleString() || 0,
    },
    {
      title: '最后使用',
      dataIndex: 'last_used_at',
      key: 'last_used_at',
      width: 150,
      render: (val: string) => val ? dayjs(val).format('MM-DD HH:mm') : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 280,
      render: (_: any, record: AccountState) => (
        <Space size="small">
          {/* Telegram账号特有：QR码授权按钮 */}
          {record.channel === 'telegram' && (
            <Tooltip title="扫码授权">
              <Button
                type="primary"
                size="small"
                icon={<QrcodeOutlined />}
                onClick={() => handleQRAuthClick(record)}
              >
                扫码授权
              </Button>
            </Tooltip>
          )}
          <Tooltip title="测试连接">
            <Button
              type="text"
              size="small"
              icon={<ApiOutlined />}
              loading={testingAccount === record.id}
              onClick={() => handleTestConnection(record.id)}
            />
          </Tooltip>
          <Tooltip title="重置配额">
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => handleResetDailyUsage(record.id)}
            />
          </Tooltip>
          {record.status === 'rate_limited' && (
            <Tooltip title="解除限制">
              <Button
                type="text"
                size="small"
                icon={<SafetyOutlined />}
                onClick={() => handleUnrestrict(record.id)}
              />
            </Tooltip>
          )}
          <Tooltip title="编辑">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除这个账户吗？"
            onConfirm={() => handleDelete(record.id)}
          >
            <Tooltip title="删除">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="account-management-page">
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="总账户数"
              value={stats.total}
              prefix={<ThunderboltOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="正常运行"
              value={stats.active}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="限流中"
              value={stats.rateLimited}
              valueStyle={{ color: '#faad14' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="异常"
              value={stats.error}
              valueStyle={{ color: '#ff4d4f' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* 账户列表 */}
      <Card
        title="发送账户管理"
        extra={
          <Space>
            <Button icon={<SyncOutlined />} onClick={() => refetch()}>
              刷新
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModalVisible(true)}
            >
              添加账户
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={accounts}
          rowKey="id"
          loading={isLoading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个账户`,
          }}
        />
      </Card>

      {/* 创建账户弹窗 - 使用Tabs选择渠道类型 */}
      <Modal
        title="添加发送账户"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
        }}
        footer={null}
        width={650}
        destroyOnClose
      >
        <Tabs
          items={[
            {
              key: 'email',
              label: (
                <Space>
                  <MailOutlined />
                  Email
                </Space>
              ),
              children: (
                <EmailAccountForm
                  onSuccess={() => {
                    setCreateModalVisible(false);
                    refetch();
                  }}
                  onCancel={() => setCreateModalVisible(false)}
                />
              ),
            },
            {
              key: 'twitter',
              label: (
                <Space>
                  <TwitterOutlined />
                  Twitter
                </Space>
              ),
              children: (
                <TwitterAccountForm
                  onSuccess={() => {
                    setCreateModalVisible(false);
                    refetch();
                  }}
                  onCancel={() => setCreateModalVisible(false)}
                />
              ),
            },
            {
              key: 'telegram',
              label: (
                <Space>
                  <SendOutlined />
                  Telegram
                </Space>
              ),
              children: (
                <>
                  <TelegramAccountForm
                    onSuccess={() => {
                      setCreateModalVisible(false);
                      refetch();
                    }}
                    onCancel={() => setCreateModalVisible(false)}
                  />
                  <Alert
                    message="创建后需使用QR码扫描登录"
                    description="创建Telegram账户后，请在账户列表中点击「扫码授权」按钮完成登录"
                    type="info"
                    showIcon
                    style={{ marginTop: 16 }}
                  />
                </>
              ),
            },
          ]}
        />
      </Modal>

      {/* 编辑账户弹窗 - 根据账户类型渲染对应表单 */}
      <Modal
        title={`编辑${selectedAccount ? channelConfig[selectedAccount.channel]?.label : ''}账户`}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
        }}
        footer={null}
        width={650}
        destroyOnClose
      >
        {selectedAccount && (
          <>
            {selectedAccount.channel === 'email' && (
              <EmailAccountForm
                account={selectedAccount}
                onSuccess={() => {
                  setEditModalVisible(false);
                  refetch();
                }}
                onCancel={() => setEditModalVisible(false)}
              />
            )}
            {selectedAccount.channel === 'twitter' && (
              <TwitterAccountForm
                account={selectedAccount}
                onSuccess={() => {
                  setEditModalVisible(false);
                  refetch();
                }}
                onCancel={() => setEditModalVisible(false)}
              />
            )}
            {selectedAccount.channel === 'telegram' && (
              <>
                <TelegramAccountForm
                  account={selectedAccount}
                  onSuccess={() => {
                    setEditModalVisible(false);
                    refetch();
                  }}
                  onCancel={() => setEditModalVisible(false)}
                />
                <div style={{ marginTop: 16 }}>
                  <Button
                    type="primary"
                    icon={<QrcodeOutlined />}
                    onClick={() => {
                      setEditModalVisible(false);
                      handleQRAuthClick(selectedAccount);
                    }}
                    block
                  >
                    重新扫码授权
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </Modal>

      {/* 账户详情抽屉 */}
      <Drawer
        title="账户详情"
        placement="right"
        width={500}
        open={detailDrawerVisible}
        onClose={() => setDetailDrawerVisible(false)}
      >
        {selectedAccount && (
          <div>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="账户名称">
                {selectedAccount.name}
              </Descriptions.Item>
              <Descriptions.Item label="渠道">
                <Tag color={channelConfig[selectedAccount.channel]?.color}>
                  {channelConfig[selectedAccount.channel]?.label}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Badge
                  status={statusConfig[selectedAccount.status]?.color as any}
                  text={statusConfig[selectedAccount.status]?.text}
                />
              </Descriptions.Item>
              <Descriptions.Item label="启用状态">
                {selectedAccount.is_active ? (
                  <Tag color="success">已启用</Tag>
                ) : (
                  <Tag color="default">已禁用</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="今日使用">
                <Progress
                  percent={Math.round(
                    (selectedAccount.daily_sent / selectedAccount.daily_limit) * 100
                  )}
                  format={() =>
                    `${selectedAccount.daily_sent} / ${selectedAccount.daily_limit}`
                  }
                />
              </Descriptions.Item>
              <Descriptions.Item label="总发送量">
                {selectedAccount.total_sent?.toLocaleString() || 0}
              </Descriptions.Item>
              <Descriptions.Item label="最后使用">
                {selectedAccount.last_used_at
                  ? dayjs(selectedAccount.last_used_at).format('YYYY-MM-DD HH:mm:ss')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {dayjs(selectedAccount.created_at).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
            </Descriptions>

            <Card title="凭证信息" size="small" style={{ marginTop: 16 }}>
              {selectedAccount.credentials &&
                Object.entries(selectedAccount.credentials).map(([key, value]) => {
                  const isSecret = key.toLowerCase().includes('password') ||
                    key.toLowerCase().includes('secret') ||
                    key.toLowerCase().includes('token');
                  const showValue = showSecrets[key];

                  return (
                    <div key={key} style={{ marginBottom: 8 }}>
                      <Text type="secondary">{key}: </Text>
                      {isSecret ? (
                        <Space>
                          <Text code>{showValue ? String(value) : '••••••••'}</Text>
                          <Button
                            type="text"
                            size="small"
                            icon={showValue ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                            onClick={() => toggleSecretVisibility(key)}
                          />
                        </Space>
                      ) : (
                        <Text code>{String(value)}</Text>
                      )}
                    </div>
                  );
                })}
            </Card>

            {selectedAccount.error_message && (
              <Alert
                message="错误信息"
                description={selectedAccount.error_message}
                type="error"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}

            <div style={{ marginTop: 24 }}>
              <Space wrap>
                <Button
                  icon={<ApiOutlined />}
                  loading={testingAccount === selectedAccount.id}
                  onClick={() => handleTestConnection(selectedAccount.id)}
                >
                  测试连接
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => handleResetDailyUsage(selectedAccount.id)}
                >
                  重置配额
                </Button>
                {selectedAccount.status === 'rate_limited' && (
                  <Button
                    icon={<SafetyOutlined />}
                    onClick={() => handleUnrestrict(selectedAccount.id)}
                  >
                    解除限制
                  </Button>
                )}
                {selectedAccount.channel === 'telegram' && (
                  <Button
                    type="primary"
                    icon={<QrcodeOutlined />}
                    onClick={() => handleQRAuthClick(selectedAccount)}
                  >
                    重新授权
                  </Button>
                )}
              </Space>
            </div>
          </div>
        )}
      </Drawer>

      {/* API凭据输入弹窗 */}
      <Modal
        title="输入Telegram API凭据"
        open={credentialsModalVisible}
        onOk={handleCredentialsConfirm}
        onCancel={() => setCredentialsModalVisible(false)}
        okText="确认并授权"
        cancelText="取消"
      >
        <Alert
          message="需要API凭据才能授权"
          description={
            <div>
              <p>请输入您的Telegram API凭据。如果还没有，请访问：</p>
              <a href="https://my.telegram.org/apps" target="_blank" rel="noopener noreferrer">
                https://my.telegram.org/apps
              </a>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form layout="vertical">
          <Form.Item label="API ID" required>
            <Input
              placeholder="请输入API ID"
              value={inputApiId}
              onChange={(e) => setInputApiId(e.target.value)}
            />
          </Form.Item>
          <Form.Item label="API Hash" required>
            <Input
              placeholder="请输入API Hash"
              value={inputApiHash}
              onChange={(e) => setInputApiHash(e.target.value)}
            />
          </Form.Item>
          <Form.Item>
            <Checkbox
              checked={saveCredentialsChecked}
              onChange={(e) => setSaveCredentialsChecked(e.target.checked)}
            >
              <Space size={4}>
                <KeyOutlined />
                保存凭据到服务器（下次授权时自动填充）
              </Space>
            </Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      {/* Telegram QR授权弹窗 */}
      <TelegramQRAuth
        visible={qrAuthVisible}
        onClose={() => setQrAuthVisible(false)}
        onSuccess={handleQRAuthSuccess}
        apiId={qrAuthApiId}
        apiHash={qrAuthApiHash}
        sessionName={qrAuthSessionName}
      />
    </div>
  );
};

export default AccountManagement;
